import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FileText, Loader2, Plus, X, AlertTriangle, Radio, ShieldCheck, Search, UserRound, Send } from "lucide-react";
import { PeppolStatusBadge } from "./StudioPeppol";
import { PeppolBadge, PeppolSummary } from "@/components/studio/PeppolBadge";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { StatusBadge, EmptyState, ListSkeleton } from "@/components/studio/StudioUI";
import { studio, apiError, beDate, eur, inputCls, btnPrimary, sym } from "@/lib/studioApi";

const FILTERS = [["tous", "Toutes"], ["brouillon", "Brouillons"], ["ouvertes", "À encaisser"], ["en_retard", "En retard"], ["payee", "Payées"]];
const OPEN_STATUSES = ["envoyee", "vue", "partielle"];
const filterKey = (status) => (OPEN_STATUSES.includes(status) ? "ouvertes" : status);
const normalizeFilter = (v) => (!v ? "tous" : OPEN_STATUSES.includes(v) ? "ouvertes" : v);

export default function StudioInvoices({ creditNotes = false }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [open, setOpen] = useState(params.get("nouveau") === "1");
  const [filter, setFilter] = useState(normalizeFilter(params.get("filtre")));
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("devis");
  const [quoteId, setQuoteId] = useState("");
  const [kind, setKind] = useState("acompte");
  const [percent, setPercent] = useState(30);
  const [clientId, setClientId] = useState("");
  const [busy, setBusy] = useState(false);
  const [peppol, setPeppol] = useState(null);

  const load = () => {
    studio.get("/invoices").then(({ data }) => setItems(data)).catch(() => toast.error("Chargement impossible."));
    studio.get("/invoices/summary").then(({ data }) => setSummary(data)).catch(() => {});
    studio.get("/peppol").then(({ data }) => setPeppol(data)).catch(() => {});
  };

  useEffect(() => {
    studio.get("/whoami").then(({ data }) => { if (!data.is_owner) return; load(); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (items === null) return;
    studio.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
    studio.get("/quotes").then(({ data }) => setQuotes(data.filter((q) => q.status !== "refused"))).catch(() => {});
  }, [items === null]);

  const createFromQuote = async () => {
    if (!quoteId) return toast.error("Choisissez un devis.");
    setBusy(true);
    try {
      const { data } = await studio.post(`/quotes/${quoteId}/invoice`, { kind, deposit_percent: Number(percent) || 30 });
      toast.success(`${data.kind_label} ${data.reference} créée.`);
      navigate(`/studio/factures/${data.id}`);
    } catch (e) { toast.error(apiError(e, "Création impossible.")); }
    finally { setBusy(false); }
  };

  const createBlank = async () => {
    if (!clientId) return toast.error("Choisissez un client.");
    setBusy(true);
    try {
      const { data } = await studio.post("/invoices", {
        client_id: clientId,
        lines: [{ description: "", quantity: 1, unit: "forfait", unit_price: 0, vat_percent: 21 }],
      });
      toast.success(`Facture ${data.reference} créée.`);
      navigate(`/studio/factures/${data.id}`);
    } catch (e) { toast.error(apiError(e, "Création impossible.")); }
    finally { setBusy(false); }
  };

  const scoped = useMemo(() => (items || []).filter((inv) => (inv.kind === "avoir") === creditNotes), [items, creditNotes]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scoped.filter((inv) => {
      if (filter !== "tous" && filterKey(inv.status) !== filter) return false;
      if (!q) return true;
      return [inv.reference, inv.client_name, inv.quote_number, inv.kind_label].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [scoped, search, filter]);

  const counts = useMemo(() => scoped.reduce((acc, inv) => {
    ["tous", filterKey(inv.status)].forEach((k) => { acc[k] = (acc[k] || 0) + 1; });
    return acc;
  }, {}), [scoped]);
  const amounts = useMemo(() => scoped.reduce((acc, inv) => {
    ["tous", filterKey(inv.status)].forEach((k) => { acc[k] = (acc[k] || 0) + Number(inv.total || 0); });
    return acc;
  }, {}), [scoped]);
  const compact = (v) => (v >= 10000 ? `${Math.round(v / 1000)}k ${sym()}` : eur(v));
  const total = visible.reduce((sum, inv) => sum + Number(inv.total || 0), 0);
  const [reminding, setReminding] = useState("");

  const remind = async (e, inv) => {
    e.preventDefault();
    e.stopPropagation();
    setReminding(inv.id);
    try {
      const { data } = await studio.post(`/invoices/${inv.id}/reminder`);
      toast.success(`Relance envoyée à ${data.client_email} avec le lien de paiement.`);
      setItems((list) => (list || []).map((x) => (x.id === inv.id ? { ...x, reminder_count: (x.reminder_count || 0) + 1, reminder_sent_at: new Date().toISOString() } : x)));
    } catch (err) { toast.error(apiError(err, "Relance impossible.")); }
    finally { setReminding(""); }
  };

  return (
    <StudioLayout
      ownerOnly
      title={creditNotes ? "Avoirs" : "Factures"}
      subtitle={creditNotes ? "Notes de crédit émises à vos clients, reliées à la facture d'origine." : "Acomptes et factures finales reliés à vos devis. Les notes de crédit sont dans Avoirs."}
      action={!creditNotes && (
        <button type="button" data-testid="new-invoice-btn" onClick={() => setOpen(!open)} className={btnPrimary}>
          {open ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {open ? "Fermer" : "Créer une facture"}
        </button>
      )}
    >
      {summary && !creditNotes && (
        <div data-testid="invoices-summary" className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            ["Facturé " + summary.year, eur(summary.invoiced_net), "HTVA, avoirs déduits"],
            ["TVA due", eur(summary.vat_due), "sur l'année"],
            ["En attente", eur(summary.open_amount), `${summary.open_count} facture(s)`],
            ["En retard", eur(summary.late_amount), `${summary.late_count} facture(s)`],
          ].map(([label, value, hint], i) => (
            <div key={label} className={`rounded-2xl border p-4 ${i === 3 && summary.late_count ? "border-red-200 bg-red-50" : "border-[#E2E7F7] bg-white"}`}>
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">{label}</p>
              <p className="mt-1 font-heading text-lg font-bold text-[#0F2645]">{value}</p>
              <p className="text-xs text-[#5C6A87]">{hint}</p>
            </div>
          ))}
        </div>
      )}

      {peppol && !peppol.can_invoice && (
        <div data-testid="peppol-gate" className="mt-6 rounded-2xl border border-[#0F2645] bg-[#0F2645] p-6 text-white shadow-[0_2px_6px_rgba(15,38,69,0.08)]">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[#93A0BC]">Étape obligatoire</p>
          <h2 className="mt-1 font-heading text-lg font-semibold inline-flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Activez votre facturation Peppol</h2>
          <p className="mt-2 text-sm text-[#C9D4F4] leading-relaxed">
            Avant de créer votre première facture, vérifiez votre numéro de TVA (base VIES) et déposez un extrait BCE ou une facture de frais.
            {peppol.status === "rejected" ? ` Votre dossier a été refusé : ${peppol.rejected_reason}.` : " Deux minutes, une seule fois."}
          </p>
          <Link to="/studio/peppol" data-testid="peppol-gate-link" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-[#0F2645] transition-colors duration-200 hover:bg-[#EEF1FC]">
            <Radio className="w-4 h-4" /> Démarrer l'activation
          </Link>
        </div>
      )}
      {peppol && peppol.can_invoice && peppol.peppol_applicable !== false && (
        <div data-testid="peppol-strip" className="mt-4 flex flex-wrap items-center gap-2 text-xs text-[#5C6A87]">
          <PeppolStatusBadge status={peppol.status} label={peppol.status_label} />
          <Link to="/studio/peppol" className="font-semibold text-[#3A5CC8] hover:underline">Détails</Link>
        </div>
      )}

      {items && <PeppolSummary items={items} />}

      {open && peppol?.can_invoice && (
        <div data-testid="new-invoice-panel" className="mt-6 rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
          <div className="flex flex-wrap gap-2">
            {[["devis", "À partir d'un devis"], ["client", "Pour un client"]].map(([v, l]) => (
              <button
                key={v}
                type="button"
                data-testid={`invoice-tab-${v}`}
                onClick={() => setTab(v)}
                className={`rounded-xl border px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                  tab === v ? "border-[#3A5CC8] bg-[#EEF1FC] text-[#3A5CC8]" : "border-[#E2E7F7] text-[#5C6A87] hover:border-[#E2E7F7]"
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          {tab === "devis" ? (
            <div className="mt-4 space-y-3">
              <select data-testid="invoice-quote-select" className={inputCls} value={quoteId} onChange={(e) => setQuoteId(e.target.value)}>
                <option value="">Choisir le devis…</option>
                {quotes.map((q) => <option key={q.id} value={q.id}>{`${q.number} — ${q.client_name} — ${eur(q.total)}`}</option>)}
              </select>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <select data-testid="invoice-kind-select" className={inputCls} value={kind} onChange={(e) => setKind(e.target.value)}>
                  <option value="acompte">Facture d'acompte</option>
                  <option value="finale">Facture finale (acompte déduit)</option>
                  <option value="facture">Facture complète</option>
                </select>
                {kind === "acompte" && (
                  <label className="flex items-center gap-2">
                    <input data-testid="invoice-deposit-percent" type="number" min="1" max="100" className={inputCls} value={percent} onChange={(e) => setPercent(e.target.value)} />
                    <span className="text-sm font-semibold text-[#5C6A87]">% d'acompte</span>
                  </label>
                )}
              </div>
              <button type="button" data-testid="invoice-create-from-quote" disabled={busy} onClick={createFromQuote} className={btnPrimary}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Créer la facture
              </button>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              <select data-testid="invoice-client-select" className={inputCls} value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">Choisir le client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{`${c.name || c.company}${c.city ? ` — ${c.city}` : ""}${c.client_type === "professionnel" ? " (pro)" : ""}`}</option>)}
              </select>
              <button type="button" data-testid="invoice-create-blank" disabled={busy} onClick={createBlank} className={btnPrimary}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Créer une facture vierge
              </button>
            </div>
          )}
        </div>
      )}

      <div data-testid="invoices-tabs" className="-mx-4 mt-6 flex gap-1 overflow-x-auto border-b border-[#E2E7F7] px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0">
        {FILTERS.map(([v, l]) => {
          const active = filter === v;
          return (
            <button
              key={v}
              type="button"
              data-testid={`invoices-filter-${v}`}
              onClick={() => setFilter(v)}
              className={`-mb-px flex shrink-0 flex-col items-start gap-0.5 border-b-2 px-3 pb-2.5 pt-1 text-left transition-colors duration-200 ${active ? "border-[#3A5CC8]" : "border-transparent hover:border-[#C7D2F5]"}`}
            >
              <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${active ? "text-[#3A5CC8]" : "text-[#0F2645]"}`}>
                {l}
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${active ? "bg-[#EEF1FC] text-[#3A5CC8]" : "bg-[#F3F5FC] text-[#5C6A87]"}`}>{counts[v] || 0}</span>
              </span>
              <span className={`font-mono text-xs tabular-nums ${active ? "text-[#3A5CC8]" : "text-[#5C6A87]"}`}>{compact(amounts[v] || 0)}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#93A0BC]" />
        <input data-testid="invoices-search" className={`${inputCls} pl-9 bg-[#F6F8FD]`} placeholder="Rechercher une facture…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <p data-testid="invoices-count" className="mt-3 text-sm text-[#5C6A87]">
        {visible.length} {creditNotes ? "avoir" : "facture"}{visible.length > 1 ? "s" : ""} · <span className="font-mono font-bold text-[#0F2645]">{eur(total)}</span> TVAC
      </p>

      <div data-testid="invoices-list" className="mt-3 overflow-hidden rounded-2xl border border-[#E2E7F7] bg-white shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
        {items === null && <ListSkeleton />}
        {visible.map((inv, i) => (
          <Link
            key={inv.id}
            to={`/studio/factures/${inv.id}`}
            data-testid={`invoice-row-${inv.reference}`}
            style={{ animationDelay: `${Math.min(i, 8) * 30}ms` }}
            className="dv-card-in group block border-b border-[#EEF1FC] px-4 py-3.5 transition-colors last:border-b-0 hover:bg-[#F6F8FD] sm:px-5"
          >
            <div className="flex items-center justify-between gap-3">
              <p className="flex min-w-0 items-center gap-2 font-heading text-sm font-extrabold text-[#0F2645]">
                <span className="truncate">{inv.reference}</span>
                <span className="hidden text-xs font-semibold text-[#93A0BC] sm:inline">· {inv.kind_label}</span>
                <PeppolBadge peppol={inv.peppol} testId={`invoice-peppol-${inv.reference}`} />
              </p>
              <p className="shrink-0 font-mono text-sm font-bold tabular-nums text-[#0F2645]">{eur(inv.total)} <span className="text-[11px] font-semibold text-[#93A0BC]">TVAC</span></p>
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-3">
              <p className="flex min-w-0 items-center gap-2 text-sm text-[#0F2645]">
                <span className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${inv.status === "en_retard" ? "bg-rose-100 text-rose-500" : inv.status === "payee" ? "bg-emerald-100 text-emerald-600" : "bg-[#E2E7F7] text-[#3A5CC8]"}`}>
                  <UserRound className="h-3.5 w-3.5" strokeWidth={2.2} />
                </span>
                <span className="truncate">
                  {inv.client_name}
                  <span className="text-[#93A0BC]"> · {inv.status === "payee" ? `payée` : `échéance ${beDate(inv.due_date)}`}{inv.quote_number ? ` · ${inv.quote_number}` : ""}</span>
                </span>
              </p>
              <div className="flex shrink-0 items-center gap-2">
                {inv.status === "en_retard" && (
                  <button
                    type="button"
                    data-testid={`invoice-remind-${inv.reference}`}
                    disabled={reminding === inv.id}
                    onClick={(e) => remind(e, inv)}
                    title={inv.reminder_count ? `${inv.reminder_count} relance(s) déjà envoyée(s)` : "Envoyer la relance avec le lien de paiement"}
                    className="inline-flex min-h-[30px] items-center gap-1 rounded-full bg-rose-600 px-2.5 text-[11px] font-bold text-white transition-colors hover:bg-rose-700 disabled:opacity-50"
                  >
                    {reminding === inv.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                    Relancer{inv.reminder_count ? ` · ${inv.reminder_count}` : ""}
                  </button>
                )}
                <StatusBadge value={inv.status} />
              </div>
            </div>
          </Link>
        ))}
        {items !== null && !visible.length && (
          <EmptyState
            testId="invoices-empty"
            icon={FileText}
            title={scoped.length ? "Aucun document ne correspond" : creditNotes ? "Aucun avoir pour l'instant" : "Aucune facture pour l'instant"}
            hint={items.length ? "Modifiez la recherche ou le filtre." : "Créez-en une depuis un devis signé ou pour un client."}
            action={!items.length && peppol?.can_invoice && (
              <button type="button" data-testid="invoices-empty-new" onClick={() => setOpen(true)} className={`${btnPrimary} mt-2`}>
                <Plus className="h-4 w-4" /> Créer une facture
              </button>
            )}
          />
        )}
      </div>

      <p className="mt-6 inline-flex items-start gap-2 text-xs text-[#5C6A87] leading-relaxed">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-amber-500" />
        Une facture envoyée ne se supprime pas : créez une note de crédit depuis la facture concernée.
      </p>
    </StudioLayout>
  );
}
